// The popup list of an autocomplete: a `listbox` that floats under its field. Tamagui's role type
// has no `listbox`, so the one element that needs it lives here, as a raw <div> in the kit.
import "./config";
import { useGestures, type SwipeDirection } from "./gestures/useGestures";
import { Row } from "./layout";
import { Body } from "./text";

export interface ListboxProps {
  id: string;
  /** The list's accessible name. */
  label: string;
  /** Closed lists stay in the document (the input's aria-controls points at them) but are hidden. */
  open?: boolean;
  /** Swipes along the list: right usually accepts the highlighted option, left dismisses it. */
  onSwipe?: Partial<Record<SwipeDirection, () => void>>;
  children: React.ReactNode;
}

export function Listbox({
  id,
  label,
  open = true,
  onSwipe,
  children,
}: ListboxProps): React.JSX.Element {
  const { style, ...bindings } = useGestures({ onSwipe }, { axis: "x", disabled: !open });
  return (
    <div
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...bindings}
      id={id}
      role="listbox"
      aria-label={label}
      hidden={!open}
      // Keeps focus in the field, so the blur that closes the list cannot eat the click. It sits on
      // this raw <div>: Tamagui drops the mouse-down that echoes a touch tap before it reaches an
      // option's own handler, and the blur then closes the list on every tap.
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      style={{
        position: "absolute",
        insetInline: 0,
        top: "calc(100% + 4px)",
        zIndex: 20,
        display: open ? "flex" : "none",
        flexDirection: "column",
        gap: 2,
        padding: 4,
        maxHeight: "min(46dvh, 320px)",
        overflowY: "auto",
        background: "var(--surface)",
        border: "var(--borderW) solid var(--lineStrong)",
        borderRadius: 12,
        boxShadow: "var(--shSheet)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export interface ListboxOptionProps {
  id: string;
  /** The suggestion the field's arrow keys are on. */
  active: boolean;
  onSelect: () => void;
  children: string;
}

export function ListboxOption({
  id,
  active,
  onSelect,
  children,
}: ListboxOptionProps): React.JSX.Element {
  return (
    <Row
      render="li"
      id={id}
      role="option"
      aria-selected={active}
      minHeight="var(--uiTarget)"
      paddingHorizontal="3"
      borderRadius="1"
      cursor="pointer"
      backgroundColor={{ default: active ? "accentSoft" : "transparent", hover: "accentSoft" }}
      onPress={onSelect}
    >
      <Body weight="strong" tone={active ? "accent" : "normal"}>
        {children}
      </Body>
    </Row>
  );
}
